import React, { useState } from "react";
import { Box, Button, List, ListItem, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ListChecks } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Soft } from "../../Common/Soft";
import { tokens } from "@/theme/tokens";
import { entryVariants, resolveTransition } from "../motion";
import type { PlanTodo } from "../../../chat/events";

export interface PlanApprovalCardProps {
  todos: PlanTodo[];
  onApprove: () => void | Promise<void>;
  onReject: () => void | Promise<void>;
  /** Approve with edited todo contents. */
  onEdit: (todos: PlanTodo[]) => void | Promise<void>;
}

type Outcome = "idle" | "approved" | "rejected";

/**
 * Blocking plan mode: the model proposed a plan and execution is held until the
 * user decides. Approve runs the plan as-is; Edit lets the user tweak the todo
 * text and approve the revised plan; Reject abandons it. Each maps to a
 * plan_decision POSTed to /steer (the parent supplies the handlers).
 */
export function PlanApprovalCard({ todos, onApprove, onReject, onEdit }: PlanApprovalCardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome>("idle");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PlanTodo[]>(todos);
  const reduce = useReducedMotion() ?? false;

  const run = (fn: () => void | Promise<void>, done?: Outcome) => async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (done) setOutcome(done);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={entryVariants}
      transition={resolveTransition(reduce)}
    >
      <Soft
        variant="raised"
        role="region"
        aria-label="Plan approval"
        sx={{
          my: 1.5,
          p: 1.5,
          boxShadow: (t) =>
            `${t.palette.mode === "light" ? tokens.neu.light.raised : tokens.neu.dark.raised}, inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.4)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, color: "primary.main" }}>
          <ListChecks size={16} aria-hidden />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Review plan
          </Typography>
        </Box>

        {editing ? (
          <List dense sx={{ py: 0 }}>
            {draft.map((t, i) => (
              <ListItem key={t.id} sx={{ px: 0, py: 0.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={t.content}
                  onChange={(e) =>
                    setDraft((prev) =>
                      prev.map((p, j) => (j === i ? { ...p, content: e.target.value } : p)),
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <List dense sx={{ py: 0, mb: 1 }}>
            {todos.map((t, i) => (
              <ListItem key={t.id} sx={{ px: 0, py: 0.25, alignItems: "flex-start", gap: 0.75 }}>
                <Typography variant="body2" sx={{ color: "text.secondary", minWidth: 18 }}>
                  {i + 1}.
                </Typography>
                <Typography variant="body2" sx={{ color: "text.primary" }}>
                  {t.content}
                </Typography>
              </ListItem>
            ))}
          </List>
        )}

        {outcome === "idle" ? (
          <Box data-ph-scope="plan_approval" sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {editing ? (
              <>
                <Button
                  size="small"
                  variant="contained"
                  data-ph="approve_edited"
                  disabled={busy}
                  onClick={run(() => onEdit(draft), "approved")}
                >
                  Save &amp; approve
                </Button>
                <Button size="small" variant="text" data-ph="cancel" disabled={busy} onClick={() => setEditing(false)}>
                  Cancel edit
                </Button>
              </>
            ) : (
              <>
                <Button size="small" variant="contained" data-ph="approve" disabled={busy} onClick={run(onApprove, "approved")}>
                  Approve
                </Button>
                <Button size="small" variant="outlined" data-ph="edit" disabled={busy} onClick={() => setEditing(true)}>
                  Edit
                </Button>
                <Button size="small" variant="text" color="error" data-ph="reject" disabled={busy} onClick={run(onReject, "rejected")}>
                  Reject
                </Button>
              </>
            )}
          </Box>
        ) : (
          <Typography
            variant="caption"
            sx={{ color: outcome === "approved" ? "success.main" : "text.secondary" }}
          >
            {outcome === "approved" ? "Plan approved — executing." : "Plan rejected."}
          </Typography>
        )}
        {error && (
          <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Soft>
    </motion.div>
  );
}
